import { useWrapAsync } from "@paroicms/front-app-log";
import { Button } from "primereact/button";
import { useEffect, useMemo, useState } from "react";
import { createRelation, updateRelation } from "../../../../../../api/application/relation";
import type { Relation } from "../../../../../../api/infrastructure/relation.formatter";
import type { APITable } from "../../../../../../api/infrastructure/table.formatter";
import { syncOpenModalDialog } from "../../../../../shared/global-tools/dialog-system";
import { showToast } from "../../../../../shared/global-tools/toast-system";
import makeConfirmDialog from "../../../../utils/ConfirmDialog/ConfirmDialog";
import Icon from "../../../../utils/Icons/Icon";
import OurDropdown from "../../../../utils/OurDropdown/OurDropdown";
import "./RelationCard.scss";

// TODO: get this from relation types api

const CARDINALITIES: {
  value: string;
  label: string;
}[] = [
  {
    value: "one_to_one",
    label: "One-to-One",
  },
  {
    value: "one_to_many",
    label: "One-to-Many",
  },
  {
    value: "many_to_one",
    label: "Many-to-One",
  },
  {
    value: "many_to_many",
    label: "Many-to-Many",
  },
];

interface RelationCardProps {
  referenceId: string;
  initCollapse?: boolean;
  relationNumber: number;
  tables: APITable[];
  relation: Relation;
  onRemoveRelation(removeRelationId: string): void;
  refreshRelationList(): void;
}

export default function RelationCard({
  referenceId,
  initCollapse,
  relationNumber,
  tables,
  relation,
  onRemoveRelation,
  refreshRelationList,
}: RelationCardProps) {
  const [collapse, setCollapse] = useState<boolean>();
  const [sourceTableId, setSourceTableId] = useState<string>();
  const [targetTableId, setTargetTableId] = useState<string>();
  const [sourceColumn, setSourceColumn] = useState<string>();
  const [targetColumn, setTargetColumn] = useState<string>();
  const [relationshipType, setRelationshipType] = useState<string>();
  const [deleting, setDeleting] = useState(false);
  const [creating, setCreating] = useState(false);
  const wrapAsync = useWrapAsync();

  const deleteRelation = () => {
    syncOpenModalDialog(makeConfirmDialog, {
      onConfirm: () => performRelationDeletion(relation.id),
      warningMessage: (
        <>
          Voulez-vous supprimer la relation <b>{relationNumber}</b> ?
        </>
      ),
    });
  };

  const performRelationDeletion = wrapAsync(async (id: string) => {
    if (targetTableId === undefined) return;
    try {
      setDeleting(true);
      onRemoveRelation(id);
      refreshRelationList();
      showToast("Relation supprimée avec succès");
    } catch (error) {
      console.error("Error deleting relation:", error);
      showToast("Echec suppression de relation", { severity: "error" });
    } finally {
      setDeleting(false);
    }
  });

  const performRelationCreation = wrapAsync(async () => {
    if (
      targetTableId === undefined ||
      sourceTableId === undefined ||
      sourceColumn === undefined ||
      targetColumn === undefined ||
      relationshipType === undefined
    ) {
      return;
    }
    try {
      setCreating(true);
      if (relation.id.includes("temp_")) {
        await createRelation(referenceId, {
          source_table_id: sourceTableId,
          target_table_id: targetTableId,
          source_column: sourceColumn,
          target_column: targetColumn,
          type: relationshipType,
        });
      } else {
        await updateRelation(referenceId, relation.id, {
          source_table_id: sourceTableId,
          target_table_id: targetTableId,
          source_column: sourceColumn,
          target_column: targetColumn,
          type: relationshipType,
        });
      }
      refreshRelationList();
      showToast("Relation enregistrée avec succès", { severity: "success" });
    } catch (error) {
      console.error("Error saving relation:", error);
      showToast("Echec enregistrement de relation", { severity: "error" });
    } finally {
      setCreating(false);
    }
  });

  useEffect(() => {
    setCollapse(initCollapse ?? true);
  }, [initCollapse]);
  useEffect(() => {
    setSourceTableId(relation.sourceTableId);
    setTargetTableId(relation.targetTableId);
    setSourceColumn(relation.sourceColumn);
    setTargetColumn(relation.targetColumn);
    setRelationshipType(relation.type);
  }, [relation]);

  const targetTablesOptions = useMemo(() => {
    const targetTables = tables.filter((table) => table.id !== sourceTableId);
    return targetTables.map((table) => ({
      value: table.id,
      label: table.label,
    }));
  }, [tables, sourceTableId]);

  const sourceTableColumns = useMemo(
    () =>
      tables
        .find((table) => table.id === sourceTableId)
        ?.columns.map((column) => ({
          value: column.name,
          label: column.label,
        })) ?? [],
    [tables, sourceTableId],
  );

  const targetTableColumns = useMemo(
    () =>
      tables
        .find((table) => table.id === targetTableId)
        ?.columns.map((column) => ({
          value: column.name,
          label: column.label,
        })) ?? [],
    [tables, targetTableId],
  );

  return (
    <div className="RelationCard">
      <div className="RelationCard-header">
        <h3 className="RelationCard-title">Relation {relationNumber}</h3>

        <div className="RelationCard-actions">
          <button
            type="button"
            className="IconBtn"
            onClick={deleteRelation}
            disabled={deleting || creating}
          >
            <Icon name="trash" color="#EF0404" />
          </button>
          <button type="button" className="IconBtn" onClick={() => setCollapse(!collapse)}>
            <Icon
              name="chevron-down"
              className={`RelationCard-chevron ${collapse ? "" : "open"}`}
              style={{ rotate: collapse ? undefined : "180deg" }}
            />
          </button>
        </div>
      </div>

      {!collapse && (
        <div className="RelationCard-body">
          <div className="RelationCard-inputGroup">
            <label className="Field w-full">
              <span className="Field-label" style={{ fontWeight: 600, fontSize: "15px" }}>
                Table source
              </span>
              <OurDropdown<string>
                initialOptions={tables.map((table) => ({
                  value: table.id,
                  label: table.label,
                }))}
                className="sm RelationCard-dropdown"
                selectedOption={sourceTableId}
                onChange={setSourceTableId}
                placeholder="Sélectionner"
              />
            </label>

            <label className="Field w-full">
              <span className="Field-label" style={{ fontWeight: 600, fontSize: "15px" }}>
                Table cible
              </span>
              <OurDropdown<string>
                initialOptions={targetTablesOptions}
                className="sm RelationCard-dropdown"
                selectedOption={targetTableId}
                onChange={setTargetTableId}
                placeholder="Sélectionner"
                disabled={sourceTableId === undefined}
              />
            </label>
          </div>

          <div className="RelationCard-inputGroup">
            <label className="Field w-full">
              <span className="Field-label" style={{ fontWeight: 600, fontSize: "15px" }}>
                Champ source
              </span>
              <OurDropdown<string>
                initialOptions={sourceTableColumns}
                className="sm RelationCard-dropdown"
                selectedOption={sourceColumn}
                onChange={setSourceColumn}
                placeholder="Sélectionner"
                disabled={sourceTableId === undefined}
              />
            </label>

            <label className="Field w-full">
              <span className="Field-label" style={{ fontWeight: 600, fontSize: "15px" }}>
                Champ cible
              </span>
              <OurDropdown<string>
                initialOptions={targetTableColumns}
                className="sm RelationCard-dropdown"
                selectedOption={targetColumn}
                onChange={setTargetColumn}
                placeholder="Sélectionner"
                disabled={sourceTableId === undefined || targetTableId === undefined}
              />
            </label>
          </div>

          <label className="Field">
            <span className="Field-label" style={{ fontWeight: 600, fontSize: "15px" }}>
              Type de relation
            </span>
            <OurDropdown<string>
              initialOptions={CARDINALITIES}
              className="sm RelationCard-dropdown"
              selectedOption={relationshipType}
              onChange={setRelationshipType}
            />
          </label>

          <div style={{ display: "flex", justifyContent: "center", marginTop: 20, width: "100%" }}>
            <Button
              type="button"
              label="Valider"
              className="RelationCard-btn Btn primary"
              onClick={performRelationCreation}
              disabled={
                creating ||
                sourceTableId === "" ||
                targetTableId === "" ||
                sourceColumn === "" ||
                targetColumn === ""
              }
            />
          </div>
        </div>
      )}
    </div>
  );
}
